import { BREAKPOINTS } from 'config/breakpoints';
import dayjs from 'dayjs';
import { isInvitePending } from 'library/helpers/personStatus';
import { Map } from 'immutable';
import accessBase from 'library/labels/access_base.json';
import isEqual from 'lodash/isEqual';
import theme from 'theme/MainTheme';
// Import atoms by DIRECT leaf path, never the barrel: this helper is pulled in by
// reduxStore/app/slice, so importing the atoms barrel here forms a slice→barrel→slice
// import cycle that TDZ-crashes with "can't access 'AppReducer' before initialization".
import Notification from 'zComponents/atoms/Notifications';

export function clearToken() {
	localStorage.removeItem('od');
	localStorage.removeItem('id_token');
}

export function getToken() {
	try {
		const idToken = localStorage.getItem('id_token');
		return new Map({ idToken });
	} catch (err) {
		clearToken();
		return new Map();
	}
}

export function arrayEqual(array1, array2) {
	return array1.sort().toString() === array2.sort().toString();
}

export function timeDifference(givenTime) {
	givenTime = new Date(givenTime);
	const milliseconds = new Date().getTime() - givenTime.getTime();
	const numberEnding = (number) => {
		return number > 1 ? 's' : '';
	};
	const number = (num) => (num > 9 ? '' + num : '0' + num);
	const getTime = () => {
		let temp = Math.floor(milliseconds / 1000);
		const years = Math.floor(temp / 31536000);
		if (years) {
			const month = number(givenTime.getUTCMonth() + 1);
			const day = number(givenTime.getUTCDate());
			const year = givenTime.getUTCFullYear() % 100;
			return `${day}-${month}-${year}`;
		}
		const days = Math.floor((temp %= 31536000) / 86400);
		if (days) {
			if (days < 28) {
				return days + ' day' + numberEnding(days);
			} else {
				const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
				const month = months[givenTime.getUTCMonth()];
				const day = number(givenTime.getUTCDate());
				return `${day} ${month}`;
			}
		}
		const hours = Math.floor((temp %= 86400) / 3600);
		if (hours) {
			return `${hours} hour${numberEnding(hours)} ago`;
		}
		const minutes = Math.floor((temp %= 3600) / 60);
		if (minutes) {
			return `${minutes} minute${numberEnding(minutes)} ago`;
		}
		return 'a few seconds ago';
	};
	return getTime();
}

export function stringToInt(value, defValue = 0) {
	if (!value) {
		return 0;
	} else if (!isNaN(value)) {
		return parseInt(value, 10);
	}
	return defValue;
}
export function stringToPosetiveInt(value, defValue = 0) {
	const val = stringToInt(value, defValue);
	return val > -1 ? val : defValue;
}

/**
 * 🛠 What does this do?
 *
 * Converts array values in an object into individual indexed keys.
 *
 * Why? Because when sending arrays in a GET request, they break.
 * For example:
 *   { deviceIDs: [101, 102] }
 * becomes:
 *   ?deviceIDs=101,102  ❌ (Not a real array)
 *
 * This function turns it into:
 *   { "deviceIDs[0]": 101, "deviceIDs[1]": 102 }
 * so the URL becomes:
 *   ?deviceIDs[0]=101&deviceIDs[1]=102 ✅
 *
 * Useful when sending filters like personIDs or deviceIDs in GET requests.
 */
export const expandArrayParams = (params) => {
	const expanded = {};
	for (const key in params) {
		if (Array.isArray(params[key])) {
			params[key].forEach((val, index) => {
				expanded[`${key}[${index}]`] = val;
			});
		} else {
			expanded[key] = params[key];
		}
	}
	return expanded;
};

/**
 * 🧹 What does this do?
 *
 * Cleans an object by:
 * - Removing empty strings, nulls, undefined values, or empty arrays.
 * - Trimming strings (removes spaces before/after).
 * - Recursively checks nested objects and arrays.
 *
 * Example:
 * {
 *   name: '  John  ',
 *   age: null,
 *   tags: [],
 *   nested: { note: '' }
 * }
 *
 * After running:
 * {
 *   name: 'John'
 * }
 *
 * Useful before sending data in API requests to avoid sending junk.
 */

export const removeEmptyOrNull = (obj) => {
	Object.keys(obj).forEach((k) => {
		if (obj[k] && typeof obj[k] === 'object') {
			if (Array.isArray(obj[k])) {
				if (obj[k].length === 0) {
					delete obj[k];
				} else {
					obj[k].forEach((item) => removeEmptyOrNull(item));
				}
			} else {
				removeEmptyOrNull(obj[k]);
			}
		} else if (!obj[k] && obj[k] !== undefined) {
			delete obj[k];
		} else if (obj[k] === undefined) {
			delete obj[k];
		} else if (typeof obj[k] === 'string') {
			obj[k] = obj[k].trim();
		}
	});
	return obj;
};
/**
 * @description
 * @author Kishan Virani
 * @date 27/04/2022
 * @param {*} textToCopy - text to copy
 * @param {*} label - label to show in toast
 **/
export const copyToClipBoard = async (textToCopy, label) => {
	try {
		await navigator.clipboard.writeText(textToCopy);
		Notification('success', `Copied ${label ? label : ''}` + textToCopy);
	} catch (err) {
		Notification('error', `Failed to Copy ${label ? label : ''}`);
	}
};

/**
 * @description Simple Function to search on the single dimensional array and setting it to the state
 * @author Kishan Virani
 * @date 31/10/2022
 * @param {*} array
 * @param {*} searchTerm
 * @param {*} columns
 * @param {*} setState
 */
export const filterFromArray = (array, searchTerm, columns, setState) => {
	const searchResult = array.filter((row) => {
		return columns.some((column) => {
			return String(row[column]).toLowerCase().includes(searchTerm.toLowerCase());
		});
	});
	setState(searchResult);
};

export const RemoveDuplicateElementsArray = (array1, array2, property) => {
	const newArray = [];
	for (let i = 0; i < array1.length; i++) {
		let same = false;
		if (property) {
			for (let j = 0; (j < array2?.length) & !same; j++) {
				if (array1[i][property] === array2[j][property] && array1[i][property] === array2[j][property])
					same = true;
			}
		} else {
			for (let j = 0; (j < array2?.length) & !same; j++) {
				if (array1[i][property] === array2[j][property] && array1[i][property] === array2[j][property])
					same = true;
			}
		}
		if (!same) newArray.push(array1[i]);
	}
	return newArray;
};

//Generate random color
export const getRandomColor = (index) => {
	const filteredTagColor = theme.palette.tagColor.filter((color) => color !== 'yellow' && color !== 'green');
	const colors = filteredTagColor;
	const colorIndex = index % colors.length; // Use modulo to cycle through colors
	return colors[colorIndex];
};

//Get display name of the person and email
export const getDisplayName = (person) => {
	// Get the display name of the person
	const displayName = person?.displayName || (isInvitePending(person) ? person?.email : 'person name missing. ');

	// Get the title of the person
	const title = isInvitePending(person)
		? "Person hasn't hopped on Zezamii yet, but their name will pop up once they accept your invite!"
		: person?.email;

	return { displayName, title };
};

/**
 * Humanizes an IR container ExternalType (PascalCase) for display:
 * 'DoorList' → 'Door List', 'LiftFloorList' → 'Lift Floor List',
 * 'PermissionGroup' → 'Permission Group'. Values that already contain spaces
 * (e.g. a typeName like 'Inner Range') pass through unchanged. Falsy → returned as-is.
 * @param {string} externalType
 * @returns {string}
 */
export const formatExternalType = (externalType) =>
	externalType ? String(externalType).replace(/([a-z0-9])([A-Z])/g, '$1 $2') : externalType;

/**
 * Returns the appropriate view based on the width.
 * @param {number} width - The width of the screen.
 * @returns {string} - The view name ('MobileView', 'DesktopView', or 'TabView').
 */
export function getView(width) {
	let newView = 'MobileView';

	if (width >= BREAKPOINTS.xl) {
		newView = 'DesktopView';
	} else if (width >= BREAKPOINTS.md) {
		newView = 'TabView';
	}

	return newView;
}

// Disable past dates
export const disabledDate = (current) => {
	return current && current < dayjs().startOf('day');
};

// Disable dates before the start date
export const disabledEndDate = (startDate, current) => {
	return startDate && current < startDate.startOf('day');
};

// Disable hours and minutes based on the current time if today is selected
export const disabledTime = (current) => {
	const now = dayjs();
	if (current && current.isSame(now, 'day')) {
		const hours = [...Array(24).keys()].slice(0, now.hour()); // Disable hours before the current hour
		const minutes = now.hour() === current.hour() ? [...Array(60).keys()].slice(0, now.minute() + 1) : []; // Disable minutes before the current minute if the current hour is selected
		return {
			disabledHours: () => hours,
			disabledMinutes: () => minutes
		};
	}
	return {};
};

// Disable hours and minutes based on the start date and current time
export const disabledEndTime = (startDate, current) => {
	if (startDate && current) {
		// If the end date is the same day as the start date
		if (current.isSame(startDate, 'day')) {
			const hours = [...Array(24).keys()].slice(0, startDate.hour());
			const minutes =
				current.hour() === startDate.hour() ? [...Array(60).keys()].slice(0, startDate.minute() + 1) : [];
			return {
				disabledHours: () => hours,
				disabledMinutes: () => minutes
			};
		}
	}
	return {};
};

// Function to check if two objects are equal for useSelector
export const reduxEqualityFunction = (a, b) => isEqual(a, b);

/**
 * Retrieves multiple texts from the selected JSON file.
 * @param {...string} keys - List of text keys to retrieve
 */

export const getLabel = (key) => {
	// Get the store state
	// ? Based on Settings from state, read the required files and get the texts
	// ? For now it's just access_base.json
	// const state = store.getState();

	// return an label from the access_base.json file
	return accessBase[key] || '';
};

export const formatPhoneNumber = (input) => {
	const raw = input.replace(/[^\d]/g, '');

	if (input.startsWith('+61')) {
		// Format international
		return `+61 ${raw.slice(2, 6)} ${raw.slice(6, 9)} ${raw.slice(9, 12)}`.trim();
	}

	if (input.startsWith('0')) {
		// Format local
		return `${raw.slice(0, 4)} ${raw.slice(4, 7)} ${raw.slice(7, 10)}`.trim();
	}

	return input;
};

/**
 * Strips characters not allowed in a person's name.
 * Allows letters (any script), digits, spaces, hyphens, apostrophes, and periods
 * (digits let names be distinguished, e.g. "Duvan 1", "Duvan 2").
 */
export const sanitizeName = (value) =>
	value ? value.replace(/[^a-zA-Z0-9À-ÿ\u00C0-\u024F\u1E00-\u1EFF\s'\-.]/g, '') : '';

/**
 * Builds a display name from firstName + lastName.
 */
export const buildDisplayName = (firstName, lastName) =>
	[firstName, lastName]
		.filter(Boolean)
		.map((s) => s.trim())
		.join(' ') || '';

// A person's name over their email, for a two-line row. `email` comes back null when it says
// nothing the name does not already (it IS the name, or there is none).
// A person with no name and no email still needs a readable row: the Integriti sync creates panel users that have
// neither (an ExternalID only), and a bare "—" reads as a rendering fault.
const unnamedLabel = (person) => (person?.externalID ? 'Unnamed panel user' : 'Unnamed person');

export const personNameAndEmail = (person) => {
	const email = person?.email?.trim() || '';
	const name = formatDisplayName(person)?.displayName?.trim() || email || unnamedLabel(person);
	return { name, email: email && email !== name ? email : null };
};

// A person picker option: name over email (the subtitle EntitySelect shows and searches).
export const personOption = (person) => {
	const { name, email } = personNameAndEmail(person);
	return { value: person.personID, label: name, subtitle: email };
};

export const formatDisplayName = (person) => {
	// Check if displayName exists
	const hasDisplayName = person?.displayName;
	// Check if either firstName or lastName exists
	const hasNames = person?.firstName || person?.lastName;

	// Return the original person object with a guaranteed displayName field
	return {
		...person,
		displayName: hasDisplayName
			? person?.displayName // Use displayName if available
			: hasNames
				? `${person?.firstName || ''} ${person?.lastName || ''}`.trim() // Use firstName and lastName if available
				: person?.email // Fallback to email
	};
};
